import { CloseModal } from 'BreetAssets';
import { modalMaxHeightConfig, modalMaxWidthConfig } from 'BreetConfig';
import { type ModalProps } from 'BreetHelpers';
import { closeModal, selectModals, selectTourStep, useAppDispatch, useAppSelector } from 'BreetRedux';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const Modal: FC<PropsWithChildren<ModalProps>> = ({
	modalId,
	manualClose,
	onClose,
	children,
	wrapperClassName,
	dialogMessage,
	variant,
	hideCloseButton,
	fullScreen,
}) => {
	const modalContentRef = useRef<HTMLDivElement>(null);
	const previouslyFocused = useRef<HTMLElement | null>(null);

	const dispatch = useAppDispatch();

	const handleContainerClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (manualClose) return;
			if (e.detail === 0) return;
			if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
				if (!document.contains(e.target as Node)) return;

				const clickedInsideIgnoredPortal = (e.target as Element).closest('[data-modal-ignore="true"]');

				if (clickedInsideIgnoredPortal) return;

				if (onClose) onClose(false);
				dispatch(closeModal(modalId));
			}
		},
		[dispatch, modalId, manualClose, onClose]
	);

	const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

	const modals = useAppSelector(selectModals);

	const tourActive = !!useAppSelector(selectTourStep);

	const currentModal = !!modals.find((modal) => modalId !== 'initialModal' && modal.id === modalId);

	useEffect(() => {
		setPortalRoot(document.getElementById('bt_modal_root'));
	}, []);

	useEffect(() => {
		if (dialogMessage && modalId === 'loadingDialog') {
			const timer = setTimeout(() => {
				const element = document.getElementById('loading-dialog-message');
				if (element) {
					element.textContent = dialogMessage;
				}
			}, 0);
			return () => clearTimeout(timer);
		}
	}, [dialogMessage, modalId]);

	const handleModalVisibility = useCallback(() => {
		if (manualClose) return;
		if (onClose) onClose(false);
		dispatch(closeModal(modalId));
	}, [dispatch, modalId, manualClose, onClose]);

	useEffect(() => {
		const contentEl = modalContentRef.current;
		if (!portalRoot || !currentModal || !contentEl) return;

		previouslyFocused.current = document.activeElement as HTMLElement | null;

		const heading = contentEl.querySelector('h1, h2, h3, h4');
		if (heading) {
			if (!heading.id) heading.id = `modal-title-${modalId}`;
			contentEl.setAttribute('aria-labelledby', heading.id);
		} else {
			contentEl.setAttribute('aria-label', 'Dialog');
		}

		const getFocusable = () =>
			Array.from(
				contentEl.querySelectorAll<HTMLElement>(
					'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
				)
			).filter((el) => el.offsetParent !== null);

		// Focus without scrolling: the right-sheet mounts off-screen (translateX(100%)) and animates in,
		// so a scroll-into-view here would lurch the page sideways and fight the entry transition.
		(getFocusable()[0] ?? contentEl).focus({ preventScroll: true });

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				if (!manualClose) {
					if (onClose) onClose(false);
					dispatch(closeModal(modalId));
				}
				return;
			}
			if (event.key !== 'Tab') return;

			const items = getFocusable();
			if (items.length === 0) {
				event.preventDefault();
				contentEl.focus();
				return;
			}
			const first = items[0];
			const last = items[items.length - 1];
			const active = document.activeElement;
			if (event.shiftKey && (active === first || active === contentEl)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown, true);
		return () => {
			document.removeEventListener('keydown', handleKeyDown, true);
			previouslyFocused.current?.focus();
		};
	}, [portalRoot, currentModal, manualClose, modalId, onClose, dispatch]);

	if (!portalRoot || !currentModal) {
		return null;
	}

	const isDialogMessage = modalId === 'loadingDialog';
	const dialogMessageClass = isDialogMessage ? ' dialogMessage' : '';
	const stateClass = ' bt_modal_state--open';
	const variantClass = variant === 'right-sheet' ? ' right-sheet' : '';
	const customClass = wrapperClassName ? ` ${wrapperClassName}` : '';
	const fullScreenClass = fullScreen ? ' fullScreen' : '';

	const className = `${stateClass}${variantClass}${customClass}${dialogMessageClass}${fullScreenClass}`;

	const modalMaxWidth = modalMaxWidthConfig[modalId];
	const modalMaxHeight = modalMaxHeightConfig[modalId];
	const modalStyle = modalMaxWidth ? { maxWidth: modalMaxWidth, maxHeight: modalMaxHeight } : undefined;

	return createPortal(
		<>
			<div className={`bt_modal_mask${stateClass}`} />

			{/* Backdrop dismissal is a pointer convenience; Escape and the close button
			    provide the keyboard path, so this overlay needs no key handler. */}
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div
				className={`button-cancel-transform bt_modal_container${className}`}
				data-testid='modalContainer'
				onClick={handleContainerClick}
			>
				<div
					className='bt_modal_content'
					style={modalStyle}
					ref={modalContentRef}
					role='dialog'
					aria-modal='true'
					tabIndex={-1}
				>
					{!hideCloseButton && !(tourActive && modalId === 'onBoardingTodoList') && (
						<div className='bt_modal_content_header'>
							<CloseModal
								aria-label='close modal'
								focusable
								role='button'
								tabIndex={0}
								onClick={handleModalVisibility}
								id={`closeBtnRef-${modalId}`}
								className={`bt_modal_close_btn${customClass} svg-dark cursor_pointer`}
							/>
						</div>
					)}
					<div className='bt_modal_content_body'>{children}</div>
				</div>
			</div>
		</>,
		portalRoot
	);
};

export default Modal;
